import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  FolderKanban,
  Globe,
  Database,
  Link2,
  ChevronRight,
  AreaChart,
  LayoutGrid,
  Globe as BuildingOutlined,
} from 'lucide-react';

const ProjectDataFlow = ({ data }) => {
  if (!data || !data.phases || data.phases.length === 0) {
    return null;
  }

  const { metadata, phases, building_objects, stats } = data;

  const etapesMap: Record<string, any> = {};
  phases.forEach((p) => {
    const etapeName = p.etape_title || p.etape_building_title || 'Bez názvu';
    if (!etapesMap[p.etape_id]) {
      etapesMap[p.etape_id] = { title: etapeName, models: {} };
    }
    if (!etapesMap[p.etape_id].models[p.phase_model_id]) {
      etapesMap[p.etape_id].models[p.phase_model_id] = {
        title: p.phase_model_title,
        phases: [],
        bos: [],
        typeCount: p.count,
      };
    }
    etapesMap[p.etape_id].models[p.phase_model_id].phases.push(p);
  });

  if (building_objects) {
    building_objects.forEach((bo) => {
      Object.values(etapesMap).forEach((etape: any) => {
        Object.values(etape.models).forEach((model: any) => {
          if (bo.models.includes(model.title)) {
            model.bos.push(bo);
          }
        });
      });
    });
  }

  return (
    <Card className="rounded-xl shadow-xs border-none bg-white overflow-hidden mt-8">
      <div className="p-4 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
        <h4 className="text-base font-semibold text-neutral-800 m-0 flex items-center">
          <Link2 className="h-4 w-4 mr-2 text-blue-500" />
          Struktura a vazby projektu
        </h4>
        <span className="text-xs text-neutral-500 uppercase tracking-widest font-bold">
          Logický datový model
        </span>
      </div>

      <CardContent className="p-8 overflow-x-auto">
        <div className="flex items-start space-x-12 min-w-max pb-4">
          {/* ROOT: AKCE S METRIKAMI */}
          <div className="flex flex-col items-center">
            <div className="p-5 rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-100 flex flex-col items-center w-64 relative">
              <FolderKanban className="h-8 w-8 mb-3" />
              <span className="text-white font-bold text-center leading-tight text-lg">
                {metadata.title}
              </span>
              <span className="text-blue-200 text-[10px] mt-1 uppercase font-bold tracking-widest mb-4">
                Hlavní Akce
              </span>

              <Separator className="my-3 border-blue-400/30" />

              <div className="w-full space-y-3">
                <div className="flex justify-between items-center px-2">
                  <div className="flex items-center gap-1">
                    <LayoutGrid className="h-4 w-4 text-blue-200" />
                    <span className="text-white text-[11px]">TypeMarky</span>
                  </div>
                  <span className="text-white font-bold text-[11px]">{stats.total_types}</span>
                </div>
                <div className="flex justify-between items-center px-2">
                  <div className="flex items-center gap-1">
                    <FolderKanban className="h-4 w-4 text-blue-200" />
                    <span className="text-white text-[11px]">Instance</span>
                  </div>
                  <span className="text-white font-bold text-[11px]">{stats.total_instances}</span>
                </div>
                <div className="flex justify-between items-center px-2">
                  <div className="flex items-center gap-1">
                    <BuildingOutlined className="text-blue-200" />
                    <span className="text-white text-[11px]">Objekty (SO)</span>
                  </div>
                  <span className="text-white font-bold text-[11px]">
                    {stats.total_building_objects}
                  </span>
                </div>
                <div className="flex justify-between items-center px-2">
                  <div className="flex items-center gap-1">
                    <AreaChart className="h-4 w-4 text-blue-200" />
                    <span className="text-white text-[11px]">Fáze</span>
                  </div>
                  <span className="text-white font-bold text-[11px]">{stats.total_phases}</span>
                </div>
                <div className="flex justify-between items-center px-2">
                  <div className="flex items-center gap-1">
                    <Database className="h-4 w-4 text-blue-200" />
                    <span className="text-white text-[11px]">Modely</span>
                  </div>
                  <span className="text-white font-bold text-[11px]">{stats.total_models}</span>
                </div>
              </div>

              <div className="absolute -right-6 top-1/2 -translate-y-1/2">
                <ChevronRight className="h-6 w-6 text-blue-200" />
              </div>
            </div>
          </div>

          {/* ETAPY A DALŠÍ VRSTVY */}
          <div className="space-y-12 grow pt-4">
            {Object.values(etapesMap).map((etape: any, eIdx) => (
              <div key={eIdx} className="flex items-start space-x-8">
                {/* ETAPA */}
                <div className="flex flex-col items-center">
                  <div className="p-4 rounded-xl bg-orange-50 border border-orange-100 flex flex-col items-center w-44 shadow-xs relative group hover:border-orange-300 transition-all">
                    <Globe className="h-5 w-5 text-orange-500 mb-1" />
                    <span className="font-semibold text-center text-xs line-clamp-2 text-neutral-800">
                      {etape.title}
                    </span>
                    <span className="text-[9px] uppercase font-bold mt-1 text-orange-400">
                      Etapa
                    </span>
                    <div className="absolute -right-4 top-1/2 -translate-y-1/2">
                      <ChevronRight className="h-4 w-4 text-orange-200" />
                    </div>
                  </div>
                </div>

                {/* MODELY V ETAPĚ */}
                <div className="flex space-x-6">
                  {Object.values(etape.models).map((model: any, mIdx) => (
                    <div key={mIdx} className="flex flex-col items-start space-y-4">
                      <div className="p-3 rounded-lg bg-cyan-50 border border-cyan-100 w-44 shadow-xs hover:shadow-md transition-all">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1 min-w-0">
                            <Database className="h-4 w-4 text-cyan-600" />
                            <span
                              className="text-[11px] font-semibold truncate w-24 text-neutral-800"
                              title={model.title}
                            >
                              {model.title}
                            </span>
                          </div>
                          <Badge
                            variant="brand"
                            size="sm"
                            className="m-0 border-cyan-200 bg-white text-cyan-700"
                          >
                            {model.typeCount} TM
                          </Badge>
                        </div>

                        <Separator className="my-2 border-cyan-100/50" />

                        <div className="space-y-1.5">
                          <div className="text-[9px] text-cyan-400 font-bold uppercase tracking-tight mb-1">
                            Výkonové fáze
                          </div>
                          <div className="flex flex-col gap-1">
                            {model.phases.map((p) => (
                              <div
                                key={p.id}
                                className="flex items-center min-w-0 bg-white/50 p-1 rounded border border-blue-50/50"
                              >
                                <div className="flex items-center gap-1 min-w-0">
                                  <Badge
                                    variant="brand"
                                    size="sm"
                                    className="text-[7px] m-0 border-none bg-blue-100 text-blue-700 px-1 py-0 rounded shrink-0"
                                  >
                                    {p.shortcut}
                                  </Badge>
                                  <span
                                    className="text-[8px] text-blue-600 truncate w-28"
                                    title={p.name_official}
                                  >
                                    {p.name_official}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {model.bos.length > 0 && (
                          <div className="mt-3 space-y-1.5">
                            <div className="text-[9px] text-purple-400 font-bold uppercase tracking-tight mb-1">
                              Stavební objekty
                            </div>
                            <div className="flex flex-col gap-1">
                              {model.bos.slice(0, 5).map((bo) => (
                                <div
                                  key={bo.entity_id}
                                  className="flex items-center space-x-1.5 min-w-0"
                                >
                                  <Badge
                                    variant="default"
                                    size="sm"
                                    className="text-[7px] m-0 border-none bg-purple-100 text-purple-700 px-1 py-0 rounded shrink-0"
                                  >
                                    {bo.building_type}-{bo.number}
                                  </Badge>
                                  <span
                                    className="text-[8px] text-gray-500 truncate"
                                    title={bo.title}
                                  >
                                    {bo.title}
                                  </span>
                                </div>
                              ))}
                              {model.bos.length > 5 && (
                                <span className="text-[8px] text-gray-400 pl-1 italic">
                                  + dalších {model.bos.length - 5} objektů
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectDataFlow;
